import axios from 'axios';
import {
    ServiceConnectionSectionData,
    ServiceMediaSectionData,
    ServicePersonalInformationData,
    ServicePostsSectionData,
    ProfileOverviewSectionData,
    Summary,
    ServiceLocationsData,
    ServiceWebMentionsData,
    InsightsSectionData,
} from './reportApi-types';
import { DateRange } from '@mui/x-date-pickers-pro';
import { Moment } from 'moment';
import { addDateRangeParams, addTagsParams, getQueryStringFromParams } from '../utils/communication';
import { MINUTE_IN_MILLI } from '../constants/constants';

const prefix = '/api/osint';

export class ReportAPI implements ReportApi {
    async getLocationsSection(
        wsId: number,
        caseId: string,
        collectionId: string,
        dateRange: DateRange<Moment>,
        tags: any,
    ): Promise<ServiceLocationsData> {
        try {
            const params = [];
            addDateRangeParams(params, dateRange);
            addTagsParams(params, tags);
            const queryString = getQueryStringFromParams(params);
            const { data } = await axios.get<ServiceLocationsData>(
                `${prefix}/workspaces/${wsId}/cases/${caseId}/collections/${collectionId}/report/locations${queryString}`,
            );
            return data;
        } catch (error) {
            return null;
        }
    }

    async getPostById(wsId: number, caseId: string, collectionId: string, postId: string, site: string) {
        try {
            const { data } = await axios.get(
                `${prefix}/workspaces/${wsId}/cases/${caseId}/collections/${collectionId}/report/posts/${postId}?site=${site}`,
            );
            return data;
        } catch (error) {
            return null;
        }
    }

    async getMediaSection(
        wsId: number,
        caseId: string,
        collectionId: string,
        body?: object,
    ): Promise<ServiceMediaSectionData> {
        try {
            const { data } = await axios.post<ServiceMediaSectionData>(
                `${prefix}/workspaces/${wsId}/cases/${caseId}/collections/${collectionId}/report/media/search?section=media`,
                body,
            );
            return data;
        } catch (error) {
            return null;
        }
    }

    async getPersonOverviewSection(
        wsId: number,
        caseId: string,
        collectionId: string,
        dateRange: DateRange<Moment>,
        tags: any,
    ): Promise<ProfileOverviewSectionData> {
        try {
            const params = [];
            addDateRangeParams(params, dateRange);
            addTagsParams(params, tags);
            const queryString = getQueryStringFromParams(params);
            const { data } = await axios.get<ProfileOverviewSectionData>(
                `${prefix}/workspaces/${wsId}/cases/${caseId}/collections/${collectionId}/report/profileOverview${queryString}`,
            );
            return data;
        } catch (error) {
            return null;
        }
    }
    async getSummarySection(
        wsId: number,
        caseId: string,
        collectionId: string,
        dateRange: DateRange<Moment>,
        tags: any,
    ): Promise<Summary> {
        try {
            const params = [];
            addDateRangeParams(params, dateRange);
            addTagsParams(params, tags);
            const queryString = getQueryStringFromParams(params);
            const { data } = await axios.get<Summary>(
                `${prefix}/workspaces/${wsId}/cases/${caseId}/collections/${collectionId}/report/summary${queryString}`,
            );
            return data;
        } catch (error) {
            return null;
        }
    }
    async getInsightsSection(
        wsId: number,
        caseId: string,
        collectionId: string,
        dateRange: DateRange<Moment>,
        tags: any,
    ): Promise<InsightsSectionData> {
        try {
            const params = [];
            addDateRangeParams(params, dateRange);
            addTagsParams(params, tags);
            const queryString = getQueryStringFromParams(params);
            const { data } = await axios.get<InsightsSectionData>(
                `${prefix}/workspaces/${wsId}/cases/${caseId}/collections/${collectionId}/report/metrics${queryString}`,
            );
            return data;
        } catch (error) {
            return null;
        }
    }
    async getSocialPresenceSection(wsId: number, caseId: string, collectionId: string, tags: any): Promise<any> {
        try {
            const params = [];
            addTagsParams(params, tags);
            const queryString = getQueryStringFromParams(params);
            const { data } = await axios.get(
                `${prefix}/workspaces/${wsId}/cases/${caseId}/collections/${collectionId}/report/socialPresence${queryString}`,
            );
            return data;
        } catch (error) {
            return null;
        }
    }
    async getConnectionsSection(
        wsId: number,
        caseId: string,
        collectionId: string,
        body?,
    ): Promise<ServiceConnectionSectionData> {
        try {
            const { data } = await axios.post<ServiceConnectionSectionData>(
                `${prefix}/workspaces/${wsId}/cases/${caseId}/collections/${collectionId}/report/connections/search?section=connections`,
                body,
            );
            return data;
        } catch (error) {
            return null;
        }
    }
    async getPersonalInformationSection(
        wsId: number,
        caseId: string,
        collectionId: string,
        dateRange: DateRange<Moment>,
        tags: any,
    ): Promise<ServicePersonalInformationData> {
        try {
            const params = [];
            addDateRangeParams(params, dateRange);
            addTagsParams(params, tags);
            const queryString = getQueryStringFromParams(params);
            const { data } = await axios.get<ServicePersonalInformationData>(
                `${prefix}/workspaces/${wsId}/cases/${caseId}/collections/${collectionId}/report/personalInformation${queryString}`,
            );
            return data;
        } catch (error) {
            return null;
        }
    }
    async getPostsSection(wsId: number, caseId: string, collectionId: string, body?): Promise<ServicePostsSectionData> {
        try {
            const { data } = await axios.post<ServicePostsSectionData>(
                `${prefix}/workspaces/${wsId}/cases/${caseId}/collections/${collectionId}/report/posts/search?section=posts`,
                body,
            );
            return data;
        } catch (error) {
            return null;
        }
    }
    async getWebMentionsSection(
        wsId: number,
        caseId: string,
        collectionId: string,
        tags: any,
    ): Promise<ServiceWebMentionsData> {
        try {
            const params = [];
            addTagsParams(params, tags);
            const queryString = getQueryStringFromParams(params);
            const { data } = await axios.get<ServiceWebMentionsData>(
                `${prefix}/workspaces/${wsId}/cases/${caseId}/collections/${collectionId}/report/webMentions${queryString}`,
            );
            return data;
        } catch (error) {
            return null;
        }
    }
    async exportPDF(wsId: number, caseId: string, collectionId: string, body?): Promise<any> {
        try {
            const { data } = await axios.post<any>(
                `${prefix}/workspaces/${wsId}/cases/${caseId}/collections/${collectionId}/report/export`,
                body,
                { timeout: 30 * MINUTE_IN_MILLI },
            );
            return data;
        } catch (error) {
            return null;
        }
    }

    async exportJSON(wsId: number, caseId: string, collectionId: string, body?): Promise<any> {
        try {
            const { data } = await axios.post<any>(
                `${prefix}/workspaces/${wsId}/cases/${caseId}/collections/${collectionId}/report/export`,
                body,
                { timeout: 30 * MINUTE_IN_MILLI },
            );
            return data;
        } catch (error) {
            return null;
        }
    }

    async fetchMoreResults(wsId: number, caseId: string, collectionId: string): Promise<any> {
        try {
            const response = await axios.get<any>(
                `${prefix}/workspaces/${wsId}/cases/${caseId}/collections/${collectionId}/moreResults?section=posts`,
            );
            return response.data;
        } catch (error) {
            throw new Error('fetch more results failed');
        }
    }

    async fetchSimilarity(upload: any, wsId: number, caseId: string, collectionId: string): Promise<any> {
        try {
            const response = await axios.post<any>(
                `${prefix}/workspaces/${wsId}/cases/${caseId}/collections/${collectionId}/search-by-image/similarity`,
                { imageId: upload.id, url: upload.url },
            );
            return response.data;
        } catch (e) {
            throw new Error('fetch similarity failed');
        }
    }

    async fetchFaces(upload: any, wsId: number, caseId: string, collectionId: string): Promise<any> {
        try {
            const response = await axios.post<any>(
                `${prefix}/workspaces/${wsId}/cases/${caseId}/collections/${collectionId}/search-by-image/faces`,
                { imageId: upload.id, url: upload.url },
            );
            return response.data;
        } catch (e) {
            throw new Error('fetch faces failed');
        }
    }

    async updateMoreResults(
        moreResultsIds: Array<string>,
        wsId: string,
        caseId: string,
        collectionId: string,
        section: string,
    ): Promise<any> {
        const { data } = await axios.post<any>(
            `${prefix}/workspaces/${wsId}/cases/${caseId}/collections/${collectionId}/moreResults`,
            { ids: moreResultsIds, section },
        );
        return data;
    }

    async uploadImage(wsId: string, caseId: string, collectionId: string, body: any, fileName: string) {
        try {
            const response: any = await axios.post<any>(
                `${prefix}/workspaces/${wsId}/cases/${caseId}/collections/${collectionId}/search-by-image/upload/${fileName}`,
                body,
                { timeout: 5 * MINUTE_IN_MILLI, headers: { 'Content-Type': 'application/octet-stream' } },
            );
            return response.data;
        } catch (error) {
            throw new Error(
                error.response?.status === 413
                    ? 'Size error: Use a file smaller than 10 MB'
                    : 'General error: Could not upload the file. Please try again.',
            );
        }
    }

    async uploadPreferencesImage(body: Uint8Array, fileName: string) {
        try {
            const response = await axios.post(`${prefix}/preferences/logo/${fileName}`, body, {
                timeout: 5 * MINUTE_IN_MILLI,
                headers: { 'Content-Type': 'application/octet-stream' },
            });
            return response.data;
        } catch (error) {
            throw new Error(
                error.response?.status === 413
                    ? 'Size error: Use a file smaller than 10 MB'
                    : 'General error: Could not upload the file. Please try again.',
            );
        }
    }

    async uploadWatchList(body: Uint8Array, fileName: string) {
        try {
            const response = await axios.post(`${prefix}/watchlist/upload/${fileName}`, body, {
                timeout: 5 * MINUTE_IN_MILLI,
                headers: { 'Content-Type': 'application/octet-stream' },
            });
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message;
            throw new Error(
                error.response?.status === 413
                    ? 'Size error: Use a file smaller than 10 MB'
                    : errorMessage
                      ? errorMessage
                      : 'General error: Could not upload the file. Please try again.',
            );
        }
    }

    async deleteWatchList(fileName: string) {
        try {
            const response = await axios.delete(`${prefix}/watchlist/${fileName}`);
            return response.data;
        } catch (error) {
            throw new Error('delete watch list failed');
        }
    }

    async getWatchList(id: string) {
        try {
            const response = await axios.get(`${prefix}/watchlist/${id}/download`, {
                responseType: 'arraybuffer',
            });
            return response.data;
        } catch (error) {
            throw new Error('get watch list failed');
        }
    }

    async getFileFromUrl(url: string) {
        try {
            const response = await axios.get(`${prefix}/${url}`);
            return response.data;
        } catch (error) {
            throw new Error('get file failed');
        }
    }

    async updatePreferencesName(name: string) {
        try {
            const response = await axios.post(`${prefix}/preferences/name`, name);
            return response.data;
        } catch (error) {
            throw new Error('update preferances name failed');
        }
    }

    async getDataByType(
        wsId: string,
        caseId: string,
        collectionId: string,
        id: any,
        type: string,
        site: string,
        queryKey: string,
    ): Promise<any> {
        try {
            const response = await axios.get<any>(
                `${prefix}/workspaces/${wsId}/cases/${caseId}/collections/${collectionId}/report/${type}/${id}?${queryKey}=${site}`,
            );
            return response.data;
        } catch (error) {
            throw new Error('fetch data type failed');
        }
    }

    async fetchPostReactions(
        wsId: number,
        caseId: string,
        collectionId: string,
        contentId: string,
        site: string,
    ): Promise<Array<object>> {
        try {
            const response = await axios.get<any>(
                `${prefix}/workspaces/${wsId}/cases/${caseId}/collections/${collectionId}/report/posts/${contentId}/reactions?site=${site}`,
            );
            return response.data;
        } catch (error) {
            throw new Error('fetch post reactions failed');
        }
    }

    async fetchPostComments(
        wsId: number,
        caseId: string,
        collectionId: string,
        contentId: string,
        site: string,
    ): Promise<Array<object>> {
        try {
            const response = await axios.get<any>(
                `${prefix}/workspaces/${wsId}/cases/${caseId}/collections/${collectionId}/report/posts/${contentId}/comments?site=${site}`,
            );
            return response.data;
        } catch (error) {
            throw new Error('fetch post comments failed');
        }
    }

    async getPreferences() {
        try {
            const { data } = await axios.get(`${prefix}/preferences`);
            return data;
        } catch (error) {
            throw new Error('get preferances failed');
        }
    }

    async getWatchLists() {
        try {
            const { data } = await axios.get(`${prefix}/watchlist`);
            return data;
        } catch (error) {
            throw new Error('get watch lists failed');
        }
    }

    async removePreferencesImage() {
        try {
            const { data } = await axios.delete(`${prefix}/preferences/logo`);
            return data;
        } catch (error) {
            throw new Error('remove preferance image failed');
        }
    }

    async fetchWatchLists(wsId: number, caseId: string, collectionId: string): Promise<Array<object>> {
        try {
            const response = await axios.get<any>(
                `${prefix}/workspaces/${wsId}/cases/${caseId}/collections/${collectionId}/report/watchlist`,
            );
            return response.data;
        } catch (error) {
            throw new Error('fetch watch lists failed');
        }
    }

    async refreshWatchLists(wsId: number, caseId: string, collectionId: string): Promise<Array<object>> {
        try {
            const response = await axios.put<any>(
                `${prefix}/workspaces/${wsId}/cases/${caseId}/collections/${collectionId}/watchlist/rerun`,
            );
            return response.data;
        } catch (error) {
            throw new Error('refresh watch lists failed');
        }
    }
}

export interface ReportApi {
    getPersonalInformationSection: (
        wsId: number,
        caseId: string,
        collectionId: string,
        dateRange?: DateRange<Moment>,
        tags?: any,
    ) => Promise<ServicePersonalInformationData>;

    getMediaSection: (
        wsId: number,
        caseId: string,
        collectionId: string,
        body?: object,
    ) => Promise<ServiceMediaSectionData>;

    getConnectionsSection: (
        wsId: number,
        caseId: string,
        collectionId: string,
        body?: object,
    ) => Promise<ServiceConnectionSectionData>;

    getPostsSection: (
        wsId: number,
        caseId: string,
        collectionId: string,
        body?: object,
    ) => Promise<ServicePostsSectionData>;

    getPersonOverviewSection: (
        wsId: number,
        caseId: string,
        collectionId: string,
        dateRange?: DateRange<Moment>,
        tags?: any,
    ) => Promise<ProfileOverviewSectionData>;

    getSummarySection: (
        wsId: number,
        caseId: string,
        collectionId: string,
        dateRange?: DateRange<Moment>,
        tags?: any,
    ) => Promise<Summary>;

    getLocationsSection: (
        wsId: number,
        caseId: string,
        collectionId: string,
        dateRange?: DateRange<Moment>,
        tags?: any,
    ) => Promise<ServiceLocationsData>;
    getWebMentionsSection: (
        wsId: number,
        caseId: string,
        collectionId: string,
        tags?: any,
    ) => Promise<ServiceWebMentionsData>;
    getInsightsSection: (
        wsId: number,
        caseId: string,
        collectionId: string,
        dateRange?: DateRange<Moment>,
        tags?: any,
    ) => Promise<InsightsSectionData>;
    getSocialPresenceSection: (wsId: number, caseId: string, collectionId: string, tags?: any) => Promise<any>;
    getPostById(wsId: number, caseId: string, collectionId: string, postId: string, site: string);
    fetchMoreResults: (wsId: number, caseId: string, collectionId: string) => Promise<any>;
    fetchSimilarity: (upload: any, wsId: number, caseId: string, collectionId: string) => Promise<any>;
    fetchFaces: (upload: any, wsId: number, caseId: string, collectionId: string) => Promise<any>;
    updateMoreResults: (
        moreResultsIds: Array<string>,
        wsId: string,
        caseId: string,
        collectionId: string,
        section: string,
    ) => Promise<any>;
    uploadImage: (wsId: string, caseId: string, collectionId: string, body: any, fileName: string) => Promise<any>;
    fetchPostReactions: (
        wsId: number,
        caseId: string,
        collectionId: string,
        contentId: string,
        site: string,
    ) => Promise<Array<object>>;
    fetchPostComments: (
        wsId: number,
        caseId: string,
        collectionId: string,
        contentId: string,
        site: string,
    ) => Promise<Array<object>>;
    fetchWatchLists: (wsId: number, caseId: string, collectionId: string) => Promise<Array<object>>;
    uploadPreferencesImage: (body: Uint8Array, fileName: string) => Promise<{ name: string; logoBase64: string }>;
}
